import {
  Fragment,
  forwardRef,
  Ref,
  useState,
  ReactElement,
  ChangeEvent,
  useEffect
} from 'react';
import {
  Link,
  Box,
  Divider,
  IconButton,
  List,
  Button,
  CircularProgress,
  ListItem,
  Grid,
  InputBase,
  Tooltip,
  Typography,
  Card,
  Dialog,
  alpha,
  Slide,
  styled,
  useTheme
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import { useTranslation } from 'react-i18next';
import ContactSupportTwoToneIcon from '@mui/icons-material/ContactSupportTwoTone';
import Scrollbar from 'src/components/Scrollbar';
import { Snackbar, Alert } from '@mui/material';
import RestoreTwoToneIcon from '@mui/icons-material/RestoreTwoTone';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';
import StarTwoToneIcon from '@mui/icons-material/StarTwoTone';
import DashboardTwoToneIcon from '@mui/icons-material/DashboardTwoTone';
import AppSettingsAltTwoToneIcon from '@mui/icons-material/AppSettingsAltTwoTone';
import KeyboardArrowRightTwoToneIcon from '@mui/icons-material/KeyboardArrowRightTwoTone';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from '../../../Hooks/LocalStorage';
import { Client } from '../../../DashboardApi/api';
import { REACT_APP_PORTAL_API_URL} from '../../../../KeyVariables';
import { SearchClient } from '../../../api/SearchClient';

const wait = (time: number): Promise<void> =>
  new Promise((res) => setTimeout(res, time));

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement<any, any> },
  ref: Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const DialogWrapper = styled(Dialog)(
  () => `
    .MuiDialog-container {
        height: auto;
    }
    
    .MuiDialog-paperScrollPaper {
        max-height: calc(100vh - 64px)
    }
`
);

const SearchInputWrapper = styled(InputBase)(
  ({ theme }) => `
    font-size: ${theme.typography.pxToRem(18)};
    padding: ${theme.spacing(2)};
    width: 100%;
`
);

const ListButton = styled(Box)(
  ({ theme }) => `
      background-color: transparent;
      color:  ${theme.colors.alpha.black[100]};
      transition: ${theme.transitions.create(['all'])};
      border: ${theme.colors.alpha.black[10]} solid 1px;
      border-radius: ${theme.general.borderRadius};
      padding: ${theme.spacing(1)};
      margin: ${theme.spacing(1, 0)};
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;

      & > div > .MuiSvgIcon-root {
        color:  ${theme.colors.alpha.black[50]};
        transition: ${theme.transitions.create(['all'])};
      }

      &:hover {
        background-color: ${alpha(theme.colors.primary.main, 0.08)};
        color:  ${theme.colors.primary.main};
        border-color: ${alpha(theme.colors.primary.main, 0.3)};

        & > div > .MuiSvgIcon-root {
          color:  ${theme.colors.primary.main};
        }
      }
`
);

const SearchInputWrapperButton = styled(Button)(
  ({ theme }) => `
  font-weight: normal;
  background: ${theme.colors.alpha.white[10]};
  border:  ${alpha(theme.colors.alpha.white[30], 0.2)} solid 1px;

  &:hover {
    background: ${alpha(theme.colors.alpha.white[30], 0.15)};
    border: ${theme.colors.alpha.white[30]} solid 1px;
  }
`
);

const BtnControl = styled(Box)(
  ({ theme }) => `
  font-weight: bold;
  text-transform: uppercase;
  font-size: 11px !important;
  background: ${alpha(theme.colors.alpha.white[100], 0.8)};
  color: ${theme.colors.alpha.black[100]};
  border-radius: ${theme.general.borderRadiusSm};
  padding: 0 6px;
  margin-left: ${theme.spacing(2)};
`
);

function HeaderSearch() {
  const { t }: { t: any } = useTranslation();
  const theme = useTheme();

  const [searchValue, setSearchValue] = useState<string>('');
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<boolean>(false);
  const [recentSearches, setRecentSearches] = useLocalStorage<string[]>('recentSearches', []);
  const [favoriteSearches, setFavoriteSearches] = useLocalStorage<string[]>('favoriteSearches', []);
  const [filteredRecentSearches, setFilteredRecentSearches] = useState<string[]>([]);
  const [filteredFavoriteSearches, setFilteredFavoriteSearches] = useState<string[]>([]);
  // const apiUrl = REACT_APP_PATIENT_API_URL;
  // const client = new SearchClient(apiUrl);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    const filteredRecents = recentSearches.filter(search => search.toLowerCase().includes(searchValue.toLowerCase()));
    setFilteredRecentSearches(filteredRecents);
    const filteredFavorites = favoriteSearches.filter(search => search.toLowerCase().includes(searchValue.toLowerCase()));
    setFilteredFavoriteSearches(filteredFavorites);
  }, [searchValue, recentSearches, favoriteSearches]);


  const updateRecentSearches = (searchTerm) => {
    const updatedRecentSearches = [searchTerm, ...recentSearches.filter(term => term !== searchTerm)].slice(0, 10);
    setRecentSearches(updatedRecentSearches);
  };

  const removeRecentSearches = (searchTerm) => {
    setRecentSearches(recentSearches.filter(term => term !== searchTerm));
  };

  const addFavoriteSearch = (searchTerm) => {
    if (!favoriteSearches.includes(searchTerm)) {
      removeRecentSearches(searchTerm)
      const updatedFavorites = [searchTerm, ...favoriteSearches];
      setFavoriteSearches(updatedFavorites);
    }
  };

  const removeFavoriteSearch = (searchTerm) => {
    setFavoriteSearches(favoriteSearches.filter(term => term !== searchTerm));
  };

  const submitSearchOnKeyPress = async (event): Promise<void> => {
    event.preventDefault();
    setSearchLoading(true);
    const searchResult = ""
    if(searchResult != "") {
      window.open(searchResult, '_self')
    }else {
      setOpenSnackbar(true);
    }
  
    updateRecentSearches(searchValue);
    setSearchLoading(false);
  };

  const submitSearch = async (search): Promise<void> => {
    setSearchLoading(true);
    const searchResult =""
    if(searchResult != "") {
      window.open(searchResult, '_self')
    }else {
      setOpenSnackbar(true);
    }
  
    setSearchLoading(false);
  };

  const navigate = useNavigate();
  const handleKeyPress = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
  
      const value = event.currentTarget.value;
  
      if (value) {
        setSearchValue(value);
        setSearchLoading(true);
        await submitSearchOnKeyPress(event);
        setSearchLoading(false);
      } else {
        setSearchValue('');
      }
    }
  };
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      {/* <SearchInputWrapperButton
        variant="contained"
        disableElevation
        onClick={handleClickOpen}
        startIcon={<SearchTwoToneIcon />}
      >
        {t('Search...')}
      </SearchInputWrapperButton> */}

      <DialogWrapper
        open={open}
        TransitionComponent={Transition}
        keepMounted
        maxWidth="sm"
        fullWidth
        scroll="paper"
        onClose={handleClose}
      >
        <Snackbar
          open={openSnackbar}
          autoHideDuration={1000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: '100%' }}>
            Nothing found.
          </Alert>
        </Snackbar>
        <Box>
          <form onSubmit={submitSearch}>
            <Box display="flex" alignItems="center">
              <Box flexGrow={1} display="flex" alignItems="center">
                <SearchTwoToneIcon
                  sx={{
                    ml: 2,
                    color: theme.colors.secondary.main
                  }}
                />
                <SearchInputWrapper
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  onKeyDown ={handleKeyPress}
                  autoFocus
                  placeholder={t('Search terms here...')}
                  fullWidth
                />
              </Box>
              <Card
                sx={{
                  ml: 'auto',
                  mr: 2,
                  py: 0.5,
                  px: 1,
                  background: theme.colors.alpha.black[10]
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="bold"
                >
                  esc
                </Typography>
              </Card>
            </Box>
          </form>
        </Box>
        <Divider />
        {searchLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              my: 5
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
              <Box
                sx={{
                  height: 510
                }}
              >
                <Scrollbar>
                  <Box pb={2} px={2}>
                    {
                      filteredRecentSearches.length > 0 ? 
                      <Typography
                      sx={{
                        pb: 0.5
                      }}
                      variant="h5"
                    >
                      {t('Recent searches')}
                    </Typography> : null
                    }
                   <Box sx={{height: 220}}>
                    <Scrollbar>
                    {
                    filteredRecentSearches.map(search => (
                      <ListButton onClick={() => submitSearch(search)}>
                        <Box display="flex" alignItems="center">
                          <RestoreTwoToneIcon
                            sx={{
                              mr: 1
                            }}
                            fontSize="small"
                          />
                          <Typography>{search}</Typography>
                        </Box>
                        <Box>
                          <Tooltip
                            placement="top"
                            arrow
                            title={t('Save this search')}
                          >
                            <IconButton onClick={(e) => {
                              e.stopPropagation();
                              addFavoriteSearch(search);
                            }}>
                              <StarTwoToneIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            placement="top"
                            arrow
                            title={t('Remove this search from history')}
                          >
                            <IconButton onClick={(e) => {
                              e.stopPropagation();
                              removeRecentSearches(search);
                            }}>
                              <CloseTwoToneIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListButton>
                    ))
                   }
                    </Scrollbar>
                   </Box>
                 
                    {
                      filteredFavoriteSearches.length > 0 ? 
                      <Typography
                      sx={{
                        pt: 2,
                        pb: 0.5
                      }}
                      variant="h5"
                    >
                      {t('Saved searches')}
                    </Typography> : null
                    }
                    <Box sx={{height: 200}}>
                      <Scrollbar>
                      {
                      filteredFavoriteSearches.map(search => (
                        <ListButton onClick={() => submitSearch(search)}>
                      <Box display="flex" alignItems="center">
                        <StarTwoToneIcon
                          sx={{
                            mr: 1
                          }}
                          fontSize="small"
                        />
                        <Typography>{search}</Typography>
                      </Box>
                      <Box>
                        <Tooltip
                          placement="top"
                          arrow
                          title={t('Remove this search from favourites')}
                        >
                           <IconButton onClick={() => removeFavoriteSearch(search)} size="small" color="error">
                            <CloseTwoToneIcon fontSize="small"/>
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListButton>
                      ))
                    }
                      </Scrollbar>
                    </Box>
                  </Box>
                </Scrollbar>
              </Box>
          </>
        )}
      </DialogWrapper>
    </>
  );
}

export default HeaderSearch;
